import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { messageApi } from "../../api/messageApi";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import {
  formatChatDateTime,
  formatConversationTimestamp,
} from "../../utils/chatDateTime";
import "./RequestMessages.css";

const getConversationActivityTime = (conversation) =>
  conversation.LastMessageAt || conversation.CreatedAt;

const getTimestamp = (value) => {
  const timestamp = Date.parse(value || "");
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortConversationsByLatestMessage = (items = []) =>
  [...items].sort(
    (first, second) =>
      getTimestamp(getConversationActivityTime(second)) -
      getTimestamp(getConversationActivityTime(first)),
  );

const statusLabel = (status = "") =>
  status.replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase());

const RequestMessages = ({ isAdmin }) => {
  const { user } = useAuth();
  const { socket } = useNotifications();
  const [searchParams] = useSearchParams();
  const requestedId = searchParams.get("requestId");
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [mobileConversationOpen, setMobileConversationOpen] = useState(
    Boolean(requestedId),
  );
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const messageLoadVersion = useRef(0);
  const messagesEndRef = useRef(null);

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingConversations(true);
    try {
      const response = await messageApi.getConversations();
      const items = sortConversationsByLatestMessage(response.data || []);
      setConversations(items);
      setSelectedId((currentId) => {
        if (requestedId && items.some((item) => item.StoreRequestID === requestedId)) {
          return requestedId;
        }
        if (currentId && items.some((item) => item.StoreRequestID === currentId)) {
          return currentId;
        }
        return items[0]?.StoreRequestID || "";
      });
      setError("");
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not load store-request conversations.",
      );
    } finally {
      setLoadingConversations(false);
    }
  }, [requestedId]);

  const loadMessages = useCallback(async (storeRequestId) => {
    const version = ++messageLoadVersion.current;
    if (!storeRequestId) {
      setMessages([]);
      setLoadingMessages(false);
      return;
    }
    setLoadingMessages(true);
    try {
      const response = await messageApi.getMessages(storeRequestId);
      if (version !== messageLoadVersion.current) return;
      const loadedMessages = response.data || [];
      setMessages(loadedMessages);
      setError("");

      const incomingType = String(user?.Role || "").toLowerCase() === "admin"
        ? "client"
        : "admin";
      const unreadMessages = loadedMessages.filter(
        (message) => !message.IsRead && message.SenderType === incomingType,
      );
      if (unreadMessages.length) {
        try {
          await Promise.all(
            unreadMessages.map((message) => messageApi.markAsRead(message.MessageID)),
          );
          if (version === messageLoadVersion.current) {
            setMessages((current) =>
              current.map((message) =>
                unreadMessages.some((unread) => unread.MessageID === message.MessageID)
                  ? { ...message, IsRead: true }
                  : message,
              ),
            );
            const lastLoadedMessage = loadedMessages.at(-1);
            if (lastLoadedMessage) {
              setConversations((current) =>
                current.map((conversation) =>
                  conversation.StoreRequestID === storeRequestId &&
                  conversation.LastMessageID === lastLoadedMessage.MessageID
                    ? { ...conversation, LastMessageIsRead: true }
                    : conversation,
                ),
              );
            }
          }
        } catch (readError) {
          if (version === messageLoadVersion.current) {
            setError(
              readError.response?.data?.error ||
                "Messages loaded, but their read status could not be updated.",
            );
          }
        }
      }
    } catch (requestError) {
      if (version === messageLoadVersion.current) {
        setMessages([]);
        setError(
          requestError.response?.data?.error ||
            "Could not load this conversation's messages.",
        );
      }
    } finally {
      if (version === messageLoadVersion.current) setLoadingMessages(false);
    }
  }, [user?.Role]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (
      requestedId &&
      conversations.some((item) => item.StoreRequestID === requestedId)
    ) {
      setMobileConversationOpen(true);
    }
  }, [conversations, requestedId]);

  useEffect(() => {
    loadMessages(selectedId);
  }, [loadMessages, selectedId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!socket) return undefined;
    const refreshConversation = ({ storeRequestId } = {}) => {
      if (storeRequestId) {
        const now = new Date().toISOString();
        setConversations((current) =>
          sortConversationsByLatestMessage(
            current.map((conversation) =>
              conversation.StoreRequestID === storeRequestId
                ? { ...conversation, LastMessageAt: now }
                : conversation,
            ),
          ),
        );
      }
      loadConversations({ silent: true });
      if (storeRequestId === selectedId) loadMessages(selectedId);
    };
    socket.on("request:updated", refreshConversation);
    return () => socket.off("request:updated", refreshConversation);
  }, [loadConversations, loadMessages, selectedId, socket]);

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!selectedId || !draft.trim()) return;
    setSending(true);
    setError("");
    try {
      const messageText = draft.trim();
      const response = await messageApi.sendMessage(selectedId, messageText);
      const sentAt = new Date().toISOString();
      setConversations((current) =>
        sortConversationsByLatestMessage(
          current.map((conversation) =>
            conversation.StoreRequestID === selectedId
              ? {
                  ...conversation,
                  LastMessage: messageText,
                  LastMessageAt: sentAt,
                  LastMessageID: response.data?.MessageID,
                  LastSenderType: currentSenderType,
                  LastMessageIsRead: true,
                }
              : conversation,
          ),
        ),
      );
      setDraft("");
      await loadMessages(selectedId);
      await loadConversations({ silent: true });
    } catch (requestError) {
      setError(
        requestError.response?.data?.error || "Could not send this message.",
      );
    } finally {
      setSending(false);
    }
  };

  const selectedConversation = conversations.find(
    (conversation) => conversation.StoreRequestID === selectedId,
  );
  const currentSenderType = String(user?.Role || "").toLowerCase() === "admin"
    ? "admin"
    : "client";
  const isMobileConversationOpen = mobileConversationOpen;

  return (
    <section className="request-messages">
      <header className="request-messages-header">
        <div>
          <h1>Store request messages</h1>
          <p>View and reply to messages about store requests.</p>
        </div>
        <button
          type="button"
          className="request-messages-refresh"
          onClick={() => {
            loadConversations();
            loadMessages(selectedId);
          }}
        >
          <i className="fas fa-rotate-right" /> Refresh
        </button>
      </header>

      {error && <p className="request-messages-error" role="alert">{error}</p>}

      <div className="request-messages-layout">
        <aside
          className={`request-messages-sidebar ${isMobileConversationOpen ? "mobile-hide" : ""}`}
          aria-label="Store request conversations"
        >
          <div className="request-messages-sidebar-header">
            <div>
              <strong>Your store requests</strong>
              <span>
                {conversations.length} conversation
                {conversations.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>
          {loadingConversations ? (
            <p className="request-messages-empty">Loading conversations…</p>
          ) : conversations.length === 0 ? (
            <p className="request-messages-empty">
              No store request conversations yet.
            </p>
          ) : (
            conversations.map((conversation) => (
              <button
                className={`request-messages-row ${selectedId === conversation.StoreRequestID ? "selected" : ""}`}
                key={conversation.StoreRequestID}
                onClick={() => {
                  setSelectedId(conversation.StoreRequestID);
                  setMobileConversationOpen(true);
                }}
                type="button"
              >
                <span className="request-messages-avatar" aria-hidden="true">
                  {isAdmin ? "🙂" : "👩🏽‍💻"}
                </span>
                <span className="request-messages-row-copy">
                  <span className="request-messages-row-heading">
                    <span className="request-messages-row-title">
                      {conversation.BusinessName || "Store request"}
                    </span>
                    <time
                      className="request-messages-row-time"
                      dateTime={conversation.LastMessageAt || conversation.CreatedAt}
                      title={formatChatDateTime(conversation.LastMessageAt || conversation.CreatedAt)}
                    >
                      {formatConversationTimestamp(
                        conversation.LastMessageAt || conversation.CreatedAt,
                      )}
                    </time>
                  </span>
                  <span className="request-messages-row-preview">
                    {conversation.LastMessage || "Conversation started"}
                  </span>
                </span>
                <span className="request-messages-row-meta">
                  {conversation.LastSenderType !== currentSenderType &&
                    (conversation.LastMessageIsRead === false ||
                      conversation.LastMessageIsRead === 0) && (
                      <small className="request-messages-unread">New reply</small>
                    )}
                  <small className="request-messages-status">
                    {statusLabel(conversation.Status)}
                  </small>
                </span>
              </button>
            ))
          )}
        </aside>

        <div className={`request-messages-thread ${!isMobileConversationOpen ? "mobile-hide" : ""}`}>
          {selectedConversation ? (
            <>
              <header className="request-messages-thread-header">
                <button
                  type="button"
                  className="request-messages-back"
                  onClick={() => {
                    setSelectedId("");
                    setMobileConversationOpen(false);
                  }}
                  aria-label="Back to store requests"
                  title="Back to store requests"
                >
                  <i className="fas fa-arrow-left" />
                </button>
                <span className="request-messages-avatar request-messages-avatar-large" aria-hidden="true">
                  {isAdmin ? "🙂" : "👩🏽‍💻"}
                </span>
                <div className="request-messages-thread-title">
                  <strong>{selectedConversation.BusinessName || "Store request"}</strong>
                  <span>
                    Request #{selectedConversation.RequestNumber || "—"}
                    {isAdmin && selectedConversation.ClientName
                      ? ` · ${selectedConversation.ClientName}`
                      : ""}
                  </span>
                </div>
                <span className="request-messages-status">
                  {statusLabel(selectedConversation.Status)}
                </span>
              </header>
              <div className="request-messages-list" role="log" aria-live="polite">
                {loadingMessages ? (
                  <p className="request-messages-empty">Loading messages…</p>
                ) : messages.length === 0 ? (
                  <p className="request-messages-empty">No messages in this conversation yet.</p>
                ) : (
                  messages.map((message) => {
                    const isOwnMessage = message.SenderType === currentSenderType;
                    return (
                      <article
                        className={`request-message ${isOwnMessage ? "own" : ""}`}
                        key={message.MessageID}
                      >
                        {!isOwnMessage && (
                          <span className="request-messages-avatar request-message-avatar" aria-hidden="true">
                            {message.SenderType === "admin" ? "👩🏽‍💻" : "🙂"}
                          </span>
                        )}
                        <div className="request-message-bubble">
                          <strong className="request-message-sender">
                            {isOwnMessage
                              ? "You"
                              : message.SenderType === "admin"
                                ? "Toora Flex Team"
                                : selectedConversation.ClientName || "Client"}
                          </strong>
                          <p>{message.Message}</p>
                          <time>{formatChatDateTime(message.CreatedAt)}</time>
                        </div>
                      </article>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
              <form className="request-messages-compose" onSubmit={sendMessage}>
                <span aria-hidden="true">💬</span>
                <textarea
                  aria-label="Message"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  maxLength={5000}
                  rows={1}
                  placeholder="Write a message…"
                />
                <button
                  type="submit"
                  disabled={sending || !draft.trim()}
                  aria-label={sending ? "Sending message" : "Send message"}
                >
                  <i className={`fas ${sending ? "fa-spinner fa-spin" : "fa-paper-plane"}`} />
                </button>
              </form>
            </>
          ) : (
            <div className="request-messages-no-selection">
              {loadingConversations
                ? "Loading conversations…"
                : "Select a store request to view its messages."}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default RequestMessages;
